import { HubConnection, HubConnectionBuilder, LogLevel } from "@microsoft/signalr"
import { navigate } from "@reach/router";
import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { getState, pause, play, seek } from "../../components/anime-viewing/VideoPlayer2";

export interface IPartyContext {
	party: Party;
	joinParty(roomName: string, password: string): Promise<void>,
	leaveParty(): Promise<void>,
	loadAnime(page: string): Promise<void>,
	play(episodeId: number): Promise<void>,
	pause(episodeId: number): Promise<void>,
	seek(episodeId: number, progress: number): Promise<void>,
	reportProgress(episodeId: number, progress: number): Promise<void>,
	finishedJoining(): void;
	timeSynced(): void;
	clearMessage(): void;
}

interface ModalMessage {
	title: string;
	message: string;
}

export const PartyContext = createContext<IPartyContext>({
	party: {
		name: undefined,
		isInParty: false,
		error: "",
		progress: undefined,
		isPlaying: undefined,
		initialStateSet: false,
		users: [],
		lastUpdate: undefined,
		disconnected: false,
		modalMessage: null,
	},
	joinParty: async () => {},
	leaveParty: async () => {},
	loadAnime: async () => {},
	play: async () => {},
	pause: async () => {},
	seek: async () => {},
	reportProgress: async () => {},
	finishedJoining: () => {},
	timeSynced: () => {},
	clearMessage: () => {},
});

const partyLog = (message?: any, ...optionalParams: any[]) => {
	console.log(message, ...optionalParams);
}

export const usePartyContext = () => {
	const [conn, setConn] = useState<HubConnection | undefined>();
	const [room, setRoom] = useState<string>();
	const [password, setPassword] = useState<string>();
	const [inParty, setInParty] = useState(false);
	const [error, setError] = useState("");
	const [progress, setProgress] = useState<number>();
	const [isPlaying, setPlaying] = useState<boolean>();
	const [users, setUsers] = useState<PartyMember[]>([]);
	const [lastUpdate, setLastUpdate] = useState<Date>();
	const [initialStateSet, setInitialStateSet] = useState(false);
	const [leftParty, setLeftParty] = useState(false);
	const [disconnected, setDisconnected] = useState(false);
	const [message, setMessage] = useState<ModalMessage | null>(null);

	useEffect(() => {
		console.error("Playing", isPlaying);
	}, [isPlaying]);

	const onMemberJoin = useCallback((id, name) => {
		partyLog(`${name} [${id}] has joined the party`);
		setUsers(x => {
			const result: PartyMember[] = [];
			for (const u of x) {
				if (u.id === id) {
					result.push({ ...u, disconnected: false });
				} else {
					result.push(u);
				}
			}

			if (!x.some(u => u.id === id)) {
				result.push({ id, name });
			}

			return result;
		});
	}, []);

	const onMemberLeave = useCallback((id, name) => {
		partyLog(`${name} [${id}] has left the party`);
		setUsers(x => {
			return (x ?? []).filter(u => u.id !== id);
		});
	}, []);

	const onMemberDisconnect = useCallback((id, name) => {
		partyLog(`${name} [${id}] has disconnected from the party`);
		setUsers(x => {
			const result: PartyMember[] = [];
			for (const u of x) {
				if (u.id === id) {
					result.push({ ...u, disconnected: true });
				} else {
					result.push(u);
				}
			}
			return result;
		});
	}, []);

	const onLoadAnime = useCallback(async (user, page) => {
		partyLog(`${user} has changed the party location to ${page}`);
		navigate(page);

		setPlaying(undefined);
		setProgress(undefined);
		setLastUpdate(undefined);
		setInitialStateSet(false);

		await conn?.invoke("Ack", { message: `Received load anime from ${user}`, room });
	}, [conn, room]);

	const onPlay = useCallback(async (user, episodeId) => {
		partyLog(`${user} started episode ${episodeId}`);

		setPlaying(true);
		setProgress(undefined);
		setLastUpdate(undefined);
		setInitialStateSet(false);

		await conn?.invoke("Ack", { message: `Received play from ${user}`, room });
	}, [conn, room]);

	const onPause = useCallback(async (user, episodeId) => {
		partyLog(`${user} paused episode ${episodeId}`);

		setPlaying(false);
		setProgress(undefined);
		setLastUpdate(undefined);
		setInitialStateSet(false);

		await conn?.invoke("Ack", { message: `Received pause from ${user}`, room });
	}, [conn, room]);

	const onSeek = useCallback(async (user, episodeId, progress, lastUpdate: string) => {
		partyLog(`${user} seeked to ${progress} on episode ${episodeId}`);

		setPlaying(undefined);
		setProgress(progress);
		setLastUpdate(new Date(lastUpdate));
		setInitialStateSet(false);

		await conn?.invoke("Ack", { message: `Received seek from ${user}`, room });
	}, [conn, room]);

	const onReportProgress = useCallback(async (user, episodeId, progress) => {
		partyLog(`${user} is currently at ${progress} on episode ${episodeId}`);
		await conn?.invoke("Ack", { message: `Received report from ${user}`, room });
	}, [conn, room]);

	const onRejected = useCallback(async (room) => {
		partyLog("Failed to join room");
		setInParty(false);
		try {
			await conn?.stop();
		} catch {}
		setRoom(undefined);
		setConn(undefined);
		setPlaying(undefined);
		setProgress(undefined);
		setLastUpdate(undefined);
		setError("Incorrect password.");
	}, [conn]);

	const onJoined = useCallback((page, episodeId, progress, isPlaying0, lastUpdate0: string, users0: any[]) => {
		partyLog("Joined room");
		partyLog("Players: " + JSON.stringify(users0.map(u => `${u.name} [${u.id}]`)));
		partyLog("Page:" + page);
		partyLog("EpId:" + episodeId);
		partyLog("Progress:" + progress);
		partyLog("IsPlaying:" + isPlaying0);
		partyLog("LastUpdate:" + lastUpdate0 + "diff: " + (new Date().getTime() - new Date(lastUpdate0).getTime())/1000);

		if (page) {
			navigate(page);
			setPlaying(isPlaying0);
			setProgress(progress);
			setLastUpdate(new Date(lastUpdate0));
			setInitialStateSet(false);
		}

		setUsers(users0);
		setInParty(true);
		setError("");
		setDisconnected(false);
		setMessage(null);
	}, []);

	const onClose = useCallback(async (error: Error | undefined) => {
		partyLog(`Disconnected from party: ${error}`);

		setInParty(false);
		setRoom(undefined);
		setConn(undefined);
	}, []);

	const onReconnecting = useCallback(() => {
		partyLog("Reconnecting to connnection.");
		setDisconnected(true);
	}, []);

	const onReconnected = useCallback(async () => {
		partyLog("Reconnected to connection. Rejoining party.");
		// Let user know in UI - Handled by onJoined
		const currentState = getState?.();
		await conn?.invoke("JoinRoom", { room, password, ...currentState });
	}, [conn, password, room]);

	useEffect(() => {
		if (!conn) { return; }
		conn.on("MemberJoin", onMemberJoin);
		conn.on("MemberLeave", onMemberLeave);
		conn.on("MemberDisconnect", onMemberDisconnect);
		conn.on("LoadAnime", onLoadAnime);
		conn.on("Play", onPlay);
		conn.on("Pause", onPause);
		conn.on("Seek", onSeek);
		conn.on("ReportProgress", onReportProgress);
		// conn.on("Rejected", onRejected);
		// conn.on("Joined", onJoined);
		conn.onclose(onClose);
		conn.onreconnecting(onReconnecting);
		conn.onreconnected(onReconnected);

		return () => {
			conn.off("MemberJoin", onMemberJoin);
			conn.off("MemberLeave", onMemberLeave);
			conn.off("MemberDisconnect", onMemberDisconnect);
			conn.off("LoadAnime", onLoadAnime);
			conn.off("Play", onPlay);
			conn.off("Pause", onPause);
			conn.off("Seek", onSeek);
			conn.off("ReportProgress", onReportProgress);
			// conn.off("Rejected", onRejected);
			// conn.off("Joined", onJoined);
			(conn as any)._closedCallbacks = [];
			(conn as any)._reconnectingCallbacks = [];
			(conn as any)._reconnectedCallbacks = [];
		};
	}, [conn, onClose, onJoined, onLoadAnime, onMemberDisconnect, onMemberJoin, onMemberLeave, onPause, onPlay, onReconnected, onReconnecting, onRejected, onReportProgress, onSeek]);

	const joinParty = useCallback(async (roomName: string, password: string) => {
		if (inParty) { partyLog(`Already in room ${room}`); return; }
		setInParty(false);
		setError("");
		setRoom(roomName);
		setPassword(password);
		setPlaying(undefined);
		setProgress(undefined);
		setLeftParty(false);
		setDisconnected(false);
		setInitialStateSet(true);

		try {
			const conn = new HubConnectionBuilder()
				.withUrl("/party")
				.configureLogging(LogLevel.Information)
				.withAutomaticReconnect()
				.build();

			conn.on("Joined", onJoined);
			conn.on("Rejected", onRejected);

			await conn.start();

			const currentState = getState?.();
			partyLog("Joining party", roomName, currentState);
			await conn.invoke("JoinRoom", { room: roomName, password, ...currentState, currentTime: new Date() });

			setConn(conn);
		} catch (e) {
			console.error(e);
			setError(JSON.stringify(e));
		}
	}, [inParty, onJoined, onRejected, room]);

	const leaveParty = useCallback(async () => {
		partyLog("Leaving party", inParty, conn, room);
		try {
			await conn?.invoke("LeaveRoom", { room });
			await conn?.stop();
		} catch (e) {
			console.error(e);
		}
		
		setInParty(false);
		setRoom(undefined);
		setConn(undefined);
		setLeftParty(true);
	}, [conn, inParty, room]);

	const loadAnime = useCallback(async (page: string) => {
		partyLog({
			action: "loadAnime",
			inParty,
			room,
			initialStateSet,
			page,
		});
		if (!inParty || !conn || !room || !initialStateSet) { return; }
		await conn.invoke("LoadAnime", { room, page });
	}, [conn, inParty, initialStateSet, room]);

	const play = useCallback(async (episodeId: number) => {
		if (!inParty || !conn || !room || !initialStateSet) { return; }
		partyLog({
			action: "play",
			inParty,
			room,
			initialStateSet,
			episodeId,
		})
		
		await conn.invoke("Play", { room, episodeId });
	}, [conn, inParty, initialStateSet, room]);

	const pause = useCallback(async (episodeId: number) => {
		if (!inParty || !conn || !room || !initialStateSet) { return; }
		partyLog({
			action: "pause",
			inParty,
			room,
			initialStateSet,
			episodeId,
		})
		
		await conn.invoke("Pause", { room, episodeId });
	}, [conn, inParty, initialStateSet, room]);

	const seek = useCallback(async (episodeId: number, progress: number) => {
		if (!inParty || !conn || !room || !initialStateSet) { return; }
		partyLog({
			action: "seek",
			inParty,
			room,
			initialStateSet,
			episodeId,
			progress,
		})
		
		await conn.invoke("Seek", { room, episodeId, progress, currentTime: new Date() });
	}, [conn, inParty, initialStateSet, room]);

	const reportProgress = useCallback(async (episodeId: number, progress: number) => {
		if (!inParty || !conn || !room || !initialStateSet) { return; }
		partyLog({
			action: "report",
			inParty,
			room,
			initialStateSet,
			episodeId,
			progress,
		})
		
		await conn.invoke("ReportProgress", { room, episodeId, progress, currentTime: new Date() });
	}, [conn, inParty, initialStateSet, room]);

	const finishedJoining = useCallback(() => {
		partyLog("Finished joining");
		setInitialStateSet(true);
		setProgress(undefined);
		setPlaying(undefined);
	}, []);

	const timeSynced = useCallback(() => {
		setProgress(undefined);
	}, []);

	useEffect(() => {
		const connectedUsers = users.filter(u => !u.disconnected);
		if (connectedUsers.length === 1) {
			const autoDisconnect = () => {
				setMessage({
					title: "Left Party",
					message: "Left the party after 5 minutes of inactivity."
				});
				leaveParty();
			};

			const timeout = setTimeout(autoDisconnect, 5 * 60 * 1000);
			return () => {
				clearTimeout(timeout);
			}
		}
	}, [leaveParty, users]);

	useEffect(() => {
		if (!message) { return; }

		const clearMessage = () => {
			setMessage(null);
		}

		const timeout = setTimeout(clearMessage, 5 * 60 * 1000);
		return () => {
			clearTimeout(timeout);
		}
	}, [message]);

	useEffect(() => {
		const autoDisconnect = () => {
			leaveParty();
		};

		window.addEventListener("beforeunload", autoDisconnect);
		return () => {
			window.removeEventListener("beforeunload", autoDisconnect);
		}
	}, [leaveParty]);

	const party = useMemo(() => {
		const p = {
			name: room,
			isInParty: inParty,
			error,
			progress,
			isPlaying,
			initialStateSet,
			users: users,
			lastUpdate,
			disconnected: disconnected,
			modalMessage: message,
		};
		console.warn(p);
		return p;
	}, [disconnected, error, inParty, initialStateSet, isPlaying, lastUpdate, message, progress, room, users]);

	return {
		party,
		joinParty,
		leaveParty,
		loadAnime,
		play,
		pause,
		seek,
		reportProgress,
		finishedJoining,
		timeSynced,
		clearMessage: () => {
			setMessage(null);
		}
	};
}

export interface InitialPartyState {
	page: string;
	episodeId: number;
	progress: number;
	isPlaying: boolean;
}

interface PartyMember {
	id: number;
	name: string;
	disconnected?: boolean;
}

interface Party {
	name: string | undefined;
	isInParty: boolean;
	error: string;
	progress: number | undefined;
	isPlaying: boolean | undefined;
	initialStateSet: boolean;
	users: PartyMember[];
	lastUpdate: Date | undefined;
	disconnected: boolean;
	modalMessage: ModalMessage | null;
}
